import { Link, NavLink } from 'react-router-dom';
import {
  Aperture,
  Building2,
  Camera,
  ClipboardList,
  CreditCard,
  Plus,
  FileText,
  Gavel,
  LayoutDashboard,
  LayoutGrid,
  ListOrdered,
  Repeat,
  ScrollText,
  Settings,
  Trophy,
  UserCircle,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { useCompetition, useMyCompetitions } from '@/hooks/useCompetitions';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useActiveCompetitionRole } from '@/hooks/useActiveCompetitionRole';
import { adminNavItems, competitionNavItems, organizerNavItems, type NavItem } from '@/lib/navigation';
import { isSuperAdmin } from '@/lib/roles';
import { competitionStatusLabel, competitionStatusTone } from '@/lib/competitions';
import { CompetitionStatus } from '@/api/types';
import { Badge } from '@/components/ui/Badge';

/** Purely decorative - keyed by label, not part of the nav data model, so it can't drift into an auth guard by accident. */
const ICONS: Record<string, LucideIcon> = {
  'Pregled tekmovanja': LayoutGrid,
  'Moji prispevki': Camera,
  'Moji rezultati': Trophy,
  Ocenjevanje: Gavel,
  Razvrstitev: ListOrdered,
  Pregled: LayoutDashboard,
  Poročila: ClipboardList,
  'Vsa tekmovanja': LayoutGrid,
  'Nastavitve organizacije': Building2,
  Plačila: CreditCard,
  'Moj profil': UserCircle,
  Uporabniki: Users,
  Organizacije: Building2,
  'Dnevnik revizije': ScrollText,
  Nastavitve: Settings,
};

function roleLabel(manage: boolean, judge: boolean, participant: boolean, admin: boolean): string {
  if (admin) return 'Skrbnik sistema';
  const roles = [manage && 'Organizator', judge && 'Sodnik', participant && 'Tekmovalec'].filter(Boolean);
  return roles.join(' · ');
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const isOpen = useUiStore((s) => s.isSidebarOpen);
  const setOpen = useUiStore((s) => s.setSidebarOpen);
  const admin = isSuperAdmin(user);

  const role = useActiveCompetitionRole();
  const competition = useCompetition(role.competitionId ?? undefined);
  const mine = useMyCompetitions();
  // Non-admins only get the organizations they belong to, so this doubles as "which clubs do I run".
  const organizations = useOrganizations({ page: 1, pageSize: 50 });
  const myOrganizations = admin ? [] : (organizations.data?.items ?? []);
  // A SuperAdmin runs every competition, whatever the per-competition context says.
  const canManage = role.canManage || (admin && Boolean(role.competitionId));

  const competitionItems =
    role.competitionId && competition.data
      ? competitionNavItems({
          competitionId: role.competitionId,
          slug: competition.data.slug,
          status: competition.data.status,
          canManage,
          isJudge: role.isJudge,
          isParticipant: role.isParticipant,
        })
      : [];
  // An organizer keeps their own pages (overview, list of competitions, reports) even with no competition selected.
  const managedCompetitions = (mine.data?.items ?? []).filter((c) => c.canManage && c.status !== CompetitionStatus.Archived);
  const managesAny = admin || managedCompetitions.length > 0 || myOrganizations.length > 0;
  if (managesAny && !competitionItems.some((i) => i.group === 'Organizator')) competitionItems.push(...organizerNavItems());
  const adminItems = adminNavItems(user?.roles);
  const known = new Set(competitionItems.map((i) => i.to));
  const platformItems = adminItems.filter((i) => !known.has(i.to));

  const groupsOf = (items: NavItem[]) =>
    Array.from(new Set(items.filter((i) => i.group).map((i) => i.group as string)));

  function renderLink(item: NavItem) {
    const Icon = ICONS[item.label] ?? LayoutGrid;
    return (
      <NavLink
        key={`${item.group}-${item.to}`}
        to={item.to}
        end={item.end}
        onClick={() => setOpen(false)}
        className={({ isActive }) =>
          `flex h-10 items-center gap-2.5 rounded-control px-2.5 text-sm font-medium transition-colors duration-fast ${
            isActive ? 'bg-surface-2 text-fg' : 'text-fg-secondary hover:bg-surface-2 hover:text-fg'
          }`
        }
      >
        <Icon size={16} className="shrink-0" aria-hidden="true" />
        {item.label}
      </NavLink>
    );
  }

  function renderGroup(title: string, items: NavItem[]) {
    return (
      <div key={title} className="mt-4 flex flex-col gap-0.5">
        <p className="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-muted">{title}</p>
        {items.filter((i) => i.group === title).map(renderLink)}
      </div>
    );
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-overlay backdrop-blur-sm md:hidden"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 transform flex-col overflow-y-auto border-r border-line bg-surface-1 transition-transform duration-base md:sticky md:top-0 md:h-screen md:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-5">
          <Aperture size={20} className="shrink-0 text-fg" aria-hidden="true" />
          <span className="font-display text-lg font-semibold tracking-tight text-fg">Zaslonka</span>
        </div>

        {/* The competition the user is working in, with the one-click way to change it (organizers pick from the list below instead). */}
        {!managesAny && (
        <div className="border-b border-line p-3">
          {competition.data ? (
            <div className="rounded-card border border-line bg-surface-0 p-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">
                {roleLabel(role.canManage, role.isJudge, role.isParticipant, admin)}
              </p>
              <p className="mt-1 line-clamp-2 text-sm font-semibold text-fg">{competition.data.name}</p>
              <div className="mt-2">
                <Badge tone={competitionStatusTone(competition.data.status)}>
                  {competitionStatusLabel(competition.data.status)}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="px-1 text-sm text-fg-muted">Tekmovanje ni izbrano.</p>
          )}
          <Link
            to="/competitions"
            onClick={() => setOpen(false)}
            className="mt-2 flex h-10 items-center gap-2.5 rounded-control px-2.5 text-sm font-medium text-accent-text transition-colors duration-fast hover:bg-surface-2"
          >
            <Repeat size={16} className="shrink-0" aria-hidden="true" />
            {competition.data ? 'Zamenjaj tekmovanje' : 'Izberi tekmovanje'}
          </Link>
        </div>
        )}

        <nav className="flex flex-1 flex-col gap-0.5 px-3 pb-4 pt-3">
          {managesAny && (
            <Link
              to="/organizer/competitions?new=1"
              onClick={() => setOpen(false)}
              className="mb-2 flex h-10 items-center justify-center gap-2 rounded-control bg-accent px-3 text-sm font-semibold text-fg-on-accent transition-colors duration-fast hover:bg-accent-hover"
            >
              <Plus size={16} aria-hidden="true" />
              Novo tekmovanje
            </Link>
          )}
          {/* An organizer gets the list of their competitions instead of one "open competition" link. */}
          {!managesAny && competitionItems.filter((i) => !i.group).map(renderLink)}
          {groupsOf(competitionItems).map((g) => renderGroup(g, competitionItems))}
          {managesAny && (
            <div className="mt-4 flex flex-col gap-0.5">
              <p className="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-muted">Tekmovanja</p>
              {managedCompetitions.map((c) => (
                <NavLink
                  key={c.competitionId}
                  to={`/competitions/${c.slug}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex min-h-10 flex-col justify-center rounded-control px-2.5 py-1.5 transition-colors duration-fast ${
                      isActive || role.competitionId === c.competitionId
                        ? 'bg-surface-2 text-fg'
                        : 'text-fg-secondary hover:bg-surface-2 hover:text-fg'
                    }`
                  }
                >
                  <span className="line-clamp-1 text-sm font-medium">{c.competitionName}</span>
                  <span className="text-[11px] text-fg-muted">{competitionStatusLabel(c.status)}</span>
                </NavLink>
              ))}
              {renderLink({ label: 'Vsa tekmovanja', to: '/organizer/competitions', end: true })}
            </div>
          )}
          {myOrganizations.map((org) => (
            <div key={org.id} className="mt-4 flex flex-col gap-0.5">
              <p className="line-clamp-1 px-2.5 pb-1 font-mono text-[11px] uppercase tracking-[0.08em] text-fg-muted">
                {myOrganizations.length > 1 ? org.name : 'Organizacija'}
              </p>
              {renderLink({ label: 'Nastavitve organizacije', to: `/organizer/organizations/${org.id}`, end: true })}
              {renderLink({ label: 'Plačila', to: `/organizer/organizations/${org.id}/payments` })}
            </div>
          ))}
          {groupsOf(platformItems).map((g) => renderGroup(g, platformItems))}

          <div className="mt-4 flex flex-col gap-0.5 border-t border-line pt-3">
            {renderLink({ label: 'Moj profil', to: '/profile' })}
            <NavLink
              to="/navodila-za-uporabo"
              onClick={() => setOpen(false)}
              className="flex h-10 items-center gap-2.5 rounded-control px-2.5 text-sm font-medium text-fg-secondary transition-colors duration-fast hover:bg-surface-2 hover:text-fg"
            >
              <FileText size={16} className="shrink-0" aria-hidden="true" />
              Navodila za uporabo
            </NavLink>
          </div>
        </nav>
      </aside>
    </>
  );
}
